<script>
  import { format } from 'svelte-i18n';
  import { Tooltip } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';

  import { assignedFields, fields, reassignedFields, selectedUser } from '@client/stores/bizcard';
  import { activeDomain } from '@client/stores/domain-settings';
  import { fields as availableFields } from '@client/stores/fields';

  import AssignedField from './assigned-field.svelte';
  import AvailableField from './available-field.svelte';

  const isLocalField = (field) => $assignedFields.find((assigned) => assigned.name === field);

  $: unavailableFields = $availableFields
    ? $fields.filter((field) => !$availableFields.includes(field) && !isLocalField(field))
    : [];

  $: unsetFields = $selectedUser ? $fields.filter((field) => !$selectedUser[field] && !isLocalField(field)) : [];

  // TODO consider moving to store, but this is pretty UI-specific to this component.
  $: [usedOrAddedFields, availableAndHiddenFields] = (() => {
    // Map of any field that has been assigned or reassigned.
    // $reassignedFields overrides the initial state from $assignedFields
    const finalAssignedStateMap = new Map();
    $assignedFields.forEach((field) => finalAssignedStateMap.set(field.name, field));
    Object.values($reassignedFields).forEach((field) => {
      const existingState = finalAssignedStateMap.get(field.name) || {};
      finalAssignedStateMap.set(field.name, { ...existingState, ...field });
    });

    // Unique field names from all 3 sources above.
    const allFieldNames = new Set([...$availableFields, ...$fields, ...Array.from(finalAssignedStateMap.keys())]);

    const available = new Map();
    const used = new Map();

    allFieldNames.forEach((name) => {
      const finalState = finalAssignedStateMap.get(name) || {};
      const fieldObject = { name, default: {}, ...finalState };
      const isInTemplate = $fields.includes(name);
      const isHidden = finalState.hidden === true;

      // Field is used if part of the template or added, and if not hidden.
      // Otherwise, it's available.
      if (!isHidden && (isInTemplate || finalAssignedStateMap.has(name))) {
        used.set(name, fieldObject);
      } else {
        available.set(name, fieldObject);
      }
    });

    const sortedUsed = Array.from(used.values()).sort((a, b) => a.name.localeCompare(b.name));
    const sortedAvailable = Array.from(available.values()).sort((a, b) => a.name.localeCompare(b.name));

    return [sortedUsed, sortedAvailable];
  })();

  // TODO replace utility classes
</script>

<div class="px-3 pt-1 pb-3">
  {#each usedOrAddedFields as assignedField}
    <AssignedField
      field={assignedField}
      modified={!!$reassignedFields[assignedField.name]}
      unavailable={unavailableFields.includes(assignedField.name)}
      unset={unsetFields.includes(assignedField.name)}
    />
  {/each}

  {#if availableAndHiddenFields.length}
    <h6 class="mt-3">
      {$format('label.AVAILABLE_FIELDS')}
      <Icon name="help-circle" id="available-fields" />
    </h6>
    <Tooltip target="available-fields">
      {@html $format('label.TOOLTIP.AVAILABLE_FIELDS', { values: { domain: $activeDomain } })}
    </Tooltip>

    {#each availableAndHiddenFields as field}
      <AvailableField {field} />
    {/each}
  {/if}
</div>
